import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import OwnerHeader from "./OwnerHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Save, Globe, Smartphone, Edit, X, Layout, Plus, Trash, DollarSign, Sparkles, RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { db, auth } from "@/firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import OwnerSidebar from "./OwnerSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import ContentLoader from "../authentication/ContentLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PricingPlan } from "@/components/requiredlegaldocuments/landing/Pricing";
import { FeatureItem, iconMap } from "@/components/requiredlegaldocuments/landing/Features";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface FooterSettings {
  companyName: string;
  description: string;
  copyrightText: string;
  showSocialLinks: boolean;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactAddress?: string;
  updatedAt: string;
  updatedBy: string;
}

interface PricingConfig {
  heading: string;
  subheading: string;
  plans: PricingPlan[];
  updatedAt: string;
  updatedBy: string;
}

interface FeaturesConfig {
  heading: string;
  subheading: string;
  features: FeatureItem[];
  updatedAt: string;
  updatedBy: string;
}

const LandingPageSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("footer");
  const [colorMode, setColorMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'light';
  });

  // Listen for dark mode changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const html = document.documentElement;
    const observer = new window.MutationObserver(() => {
      setColorMode(html.classList.contains('dark') ? 'dark' : 'light');
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const [settings, setSettings] = useState<FooterSettings>({
    companyName: "Sonoma Cloud",
    description: "Modern school management for the digital age.",
    copyrightText: `© ${new Date().getFullYear()} Sonoma Cloud. All rights reserved.`,
    showSocialLinks: true,
    facebookUrl: "",
    twitterUrl: "",
    instagramUrl: "",
    linkedinUrl: "",
    contactEmail: "hello@sonoma.com",
    contactPhone: "(555) 123-4567",
    contactAddress: "San Francisco, CA",
    updatedAt: "",
    updatedBy: "",
  });

  const [pricingConfig, setPricingConfig] = useState<PricingConfig>({
    heading: "Simple, transparent pricing",
    subheading: "Choose the plan that fits your school's needs. No hidden fees.",
    plans: [],
    updatedAt: "",
    updatedBy: "",
  });

  const [featuresConfig, setFeaturesConfig] = useState<FeaturesConfig>({
    heading: "Everything you need",
    subheading: "Powerful features designed to streamline every aspect of school administration.",
    features: [],
    updatedAt: "",
    updatedBy: "",
  });

  // Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in as owner to access landing page settings.",
          variant: "destructive",
          duration: 5000,
        });
        window.location.href = "/login-page";
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const claims = idTokenResult.claims;

        // Check if user has owner role
        const affiliations = (claims.affiliations as any[]) || [];
        const defaultAff = claims.defaultAffiliation as any;
        
        const hasOwnerRole = defaultAff?.role === "owner" || 
                            affiliations.some(aff => aff.role === "owner");

        if (!hasOwnerRole) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access these settings.",
            variant: "destructive",
            duration: 5000,
          });
          window.location.href = "/owner-dashboard";
          return;
        }

        setIsOwner(true);
        setCurrentUser(user);
        await Promise.all([loadSettings(), loadPricing(), loadFeatures()]);
      } catch (error) {
        console.error("Error verifying owner access:", error);
        toast({
          title: "Error",
          description: "Failed to verify access. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [toast]);

  // Loaders
  const loadSettings = async () => {
    try {
      const configRef = doc(db, "systemConfig/landingPage");
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        const data = configSnap.data() as FooterSettings;
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const loadPricing = async () => {
    try {
        // Try to load draft first
        const draftRef = doc(db, "systemConfig/pricing_draft");
        const draftSnap = await getDoc(draftRef);
        
        if (draftSnap.exists()) {
             const data = draftSnap.data() as PricingConfig;
             setPricingConfig(prev => ({ ...prev, ...data, plans: data.plans || [] }));
        } else {
            // Fallback to live data if no draft
            const configRef = doc(db, "systemConfig/pricing");
            const configSnap = await getDoc(configRef);
            if (configSnap.exists()) {
                const data = configSnap.data() as PricingConfig;
                setPricingConfig(prev => ({ ...prev, ...data, plans: data.plans || [] }));
            } else {
             // Init defaults if not exists
             const defaultPlans: PricingPlan[] = [
                {
                    id: "free",
                    name: "Free",
                    badge: "CURRENT",
                    price: "$0",
                    capacity: "Up to 50 students",
                    includesText: "Perfect for small schools:",
                    features: ["Basic attendance tracking", "1 admin account", "Email support", "Core reporting features"],
                    cta: "Current plan",
                    ctaStyle: "muted",
                    isCurrent: true
                },
                {
                    id: "starter",
                    name: "Starter",
                    badge: "",
                    price: "$19",
                    capacity: "Up to 200 students",
                    includesText: "Everything in Free, plus:",
                    features: ["5 staff accounts", "Basic analytics dashboard", "SMS notifications", "Priority email support"],
                    cta: "Upgrade to Starter",
                    ctaStyle: "primary",
                    isCurrent: false
                },
                {
                    id: "pro",
                    name: "Professional",
                    badge: "POPULAR",
                    price: "$49",
                    capacity: "Up to 1,000 students",
                    includesText: "Everything in Starter, plus:",
                    features: ["Unlimited staff accounts", "Advanced analytics", "Priority phone support", "API access"],
                    cta: "Upgrade to Professional",
                    ctaStyle: "primary",
                    isCurrent: false
                },
                {
                    id: "enterprise",
                    name: "Enterprise",
                    badge: "",
                    price: "$99",
                    capacity: "Unlimited students",
                    includesText: "Everything in Professional, plus:",
                    features: ["Dedicated success manager", "Custom integrations", "99.9% SLA guarantee", "On-premise deployment"],
                    cta: "Contact Sales",
                    ctaStyle: "primary",
                    isCurrent: false
                },
            ];
            setPricingConfig(prev => ({ ...prev, plans: defaultPlans }));
        }
      }
    } catch(err) {
        console.error("Error loading pricing", err);
    }
  }

  const loadFeatures = async () => {
    try {
        const configRef = doc(db, "systemConfig/features");
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
            const data = configSnap.data() as FeaturesConfig;
            setFeaturesConfig(prev => ({ ...prev, ...data, features: data.features || [] }));
        } else {
            // Default setup if nothing in DB
             const defaultFeatures: FeatureItem[] = [
                {
                  id: "1",
                  icon: "BarChart3",
                  title: "Real-time Analytics",
                  description: "Get instant insights into attendance, performance, and engagement with comprehensive dashboards."
                },
                {
                  id: "2",
                  icon: "Users",
                  title: "Student Management",
                  description: "Complete student lifecycle management from enrollment to graduation, all in one place."
                },
                {
                  id: "3",
                  icon: "Shield",
                  title: "Secure & Compliant",
                  description: "Enterprise-grade security with full FERPA compliance and data protection standards."
                },
                {
                  id: "4",
                  icon: "MessageSquare",
                  title: "Communication Hub",
                  description: "Real-time messaging between teachers, parents, and staff with instant notifications."
                },
                {
                  id: "5",
                  icon: "CreditCard",
                  title: "Financial Tracking",
                  description: "Manage fees, payments, and budgets with automated invoicing and reporting."
                },
                {
                  id: "6",
                  icon: "Calendar",
                  title: "Smart Scheduling",
                  description: "Automated timetable generation and conflict resolution for classes and exams."
                },
                {
                  id: "7",
                  icon: "BookOpen",
                  title: "Library System",
                  description: "Digital cataloging, circulation management, and integration with student IDs."
                },
                {
                  id: "8",
                  icon: "GraduationCap",
                  title: "Academic Records",
                  description: "Digital transcripts, grading systems, and report card generation in a few clicks."
                }
            ];
             setFeaturesConfig(prev => ({ ...prev, features: defaultFeatures }));
        }
    } catch(err) {
        console.error("Error loading features", err);
    }
  }


  // Save handlers
  const saveSettings = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const configRef = doc(db, "systemConfig/landingPage");
      const configData: FooterSettings = { ...settings, updatedAt: new Date().toISOString(), updatedBy: currentUser.uid };
      await setDoc(configRef, configData, { merge: true });
      toast({ title: "Success", description: "Footer settings saved successfully." });
      setIsEditing(false);
    } catch (error) {
      toast({ title: "Error", description: "Failed to save settings.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const savePricingDraft = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
        const configRef = doc(db, "systemConfig/pricing_draft");
        const configData: PricingConfig = { ...pricingConfig, updatedAt: new Date().toISOString(), updatedBy: currentUser.uid };
        await setDoc(configRef, configData, { merge: true });
        toast({ title: "Draft Saved", description: "Pricing plans saved as draft. Click Publish to go live." });
        // Don't close editing mode on draft save, so they can keep refining or publish
    } catch (error) {
        toast({ title: "Error", description: "Failed to save draft.", variant: "destructive" });
    } finally {
        setSaving(false);
    }
  }

  const publishPricing = async () => {
      if (!currentUser) return;
      setPublishing(true);
      try {
          // Save to live
          const liveRef = doc(db, "systemConfig/pricing");
          const configData: PricingConfig = { ...pricingConfig, updatedAt: new Date().toISOString(), updatedBy: currentUser.uid };
          await setDoc(liveRef, configData, { merge: true });
          
          // Also update draft to match (sync)
          const draftRef = doc(db, "systemConfig/pricing_draft");
          await setDoc(draftRef, configData, { merge: true });

          toast({ title: "Published", description: "Pricing plans are now live on the landing page." });
          setIsEditing(false);
      } catch (error) {
          toast({ title: "Error", description: "Failed to publish pricing.", variant: "destructive" });
      } finally {
          setPublishing(false);
      }
  }

  const saveFeatures = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
        const configRef = doc(db, "systemConfig/features");
        const configData: FeaturesConfig = { ...featuresConfig, updatedAt: new Date().toISOString(), updatedBy: currentUser.uid };
        await setDoc(configRef, configData, { merge: true });
        toast({ title: "Success", description: "Features saved successfully." });
        setIsEditing(false);
    } catch (error) {
        toast({ title: "Error", description: "Failed to save features.", variant: "destructive" });
    } finally {
        setSaving(false);
    }
  }

  // Update helpers
  const updatePlan = (index: number, field: keyof PricingPlan, value: any) => {
    const newPlans = [...pricingConfig.plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setPricingConfig({ ...pricingConfig, plans: newPlans });
  };
   const updateFeatureInPlan = (planIndex: number, featureIndex: number, value: string) => { // Renamed to avoid conflict
    const newPlans = [...pricingConfig.plans];
    const newFeatures = [...newPlans[planIndex].features];
    newFeatures[featureIndex] = value;
    newPlans[planIndex].features = newFeatures;
    setPricingConfig({ ...pricingConfig, plans: newPlans });
  };
  const addFeatureToPlan = (planIndex: number) => { // Renamed
    const newPlans = [...pricingConfig.plans];
    newPlans[planIndex].features = [...newPlans[planIndex].features, "New Feature"];
    setPricingConfig({ ...pricingConfig, plans: newPlans });
  };
  const removeFeatureFromPlan = (planIndex: number, featureIndex: number) => { // Renamed
    const newPlans = [...pricingConfig.plans];
    newPlans[planIndex].features = newPlans[planIndex].features.filter((_, i) => i !== featureIndex);
    setPricingConfig({ ...pricingConfig, plans: newPlans });
  };
  const addPlan = () => {
    const newPlan: PricingPlan = {
        id: crypto.randomUUID(),
        name: "New Plan", price: "$0", capacity: "Capacity description", includesText: "Includes:", features: ["Feature 1"],
        badge: "", cta: "Get Started", ctaStyle: "primary", isCurrent: false
    };
    setPricingConfig({ ...pricingConfig, plans: [...pricingConfig.plans, newPlan] });
  };
  const removePlan = (index: number) => {
    const newPlans = pricingConfig.plans.filter((_, i) => i !== index);
    setPricingConfig({ ...pricingConfig, plans: newPlans });
  }

  // Features helpers
  const updateFeatureItem = (index: number, field: keyof FeatureItem, value: any) => {
      const newFeatures = [...featuresConfig.features];
      newFeatures[index] = { ...newFeatures[index], [field]: value };
      setFeaturesConfig({ ...featuresConfig, features: newFeatures });
  }
  const addFeatureItem = () => {
      const newFeature: FeatureItem = {
          id: crypto.randomUUID(),
          title: "New Feature",
          description: "Description of the feature.",
          icon: "BarChart3"
      };
      setFeaturesConfig({ ...featuresConfig, features: [...featuresConfig.features, newFeature] });
  }
  const removeFeatureItem = (index: number) => {
      const newFeatures = featuresConfig.features.filter((_, i) => i !== index);
      setFeaturesConfig({ ...featuresConfig, features: newFeatures });
  }

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
          <OwnerSidebar userRole="owner" activeSection="landing-settings" onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <OwnerHeader />
            <main className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-6">
               <div className="flex items-center justify-center h-96">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
               </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!isOwner) return null;

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <OwnerSidebar userRole="owner" activeSection="landing-settings" onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <OwnerHeader />
          <main className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-6">
              {/* Content when loaded */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Landing Page Control</h1>
                        <p className="text-muted-foreground mt-1">Manage content and appearance of the public landing page.</p>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid w-full grid-cols-3 max-w-[600px]">
                        <TabsTrigger value="footer">Footer</TabsTrigger>
                        <TabsTrigger value="features">Features</TabsTrigger>
                        <TabsTrigger value="pricing">Pricing</TabsTrigger>
                    </TabsList>

                    {/* Footer Tab */}
                    <TabsContent value="footer" className="space-y-6">
                         <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                             <CardHeader>
                                 <div className="flex items-center justify-between">
                                     <div className="space-y-1">
                                         <CardTitle className="text-xl flex items-center gap-2">
                                             <Layout className="h-5 w-5 text-blue-500" />
                                             Footer Settings
                                         </CardTitle>
                                         <CardDescription>Customize the footer content.</CardDescription>
                                     </div>
                                     {!isEditing ? (
                                         <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" className="gap-2"><Edit className="h-4 w-4" /> Edit</Button>
                                     ) : (
                                         <Button onClick={() => { setIsEditing(false); loadSettings(); }} variant="ghost" size="sm" className="gap-2 text-red-500"><X className="h-4 w-4" /> Cancel</Button>
                                     )}
                                 </div>
                             </CardHeader>
                             <CardContent className="space-y-6">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Company Name</Label>
                                        <Input 
                                            value={settings.companyName} 
                                            onChange={(e) => setSettings({...settings, companyName: e.target.value})} 
                                            disabled={!isEditing}
                                            placeholder="e.g. Sonoma Cloud" 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Copyright Text</Label>
                                        <Input 
                                            value={settings.copyrightText} 
                                            onChange={(e) => setSettings({...settings, copyrightText: e.target.value})} 
                                            disabled={!isEditing} 
                                            placeholder="e.g. © 2026 Sonoma Cloud. All rights reserved."
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Footer Description</Label>
                                    <Textarea 
                                        value={settings.description} 
                                        onChange={(e) => setSettings({...settings, description: e.target.value})} 
                                        disabled={!isEditing} 
                                        rows={3} 
                                        placeholder="e.g. Empowering education through innovative cloud solutions."
                                    />
                                </div>
                                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <h3 className="text-sm font-medium mb-4 flex items-center gap-2"><Smartphone className="h-4 w-4" /> Contact Information</h3>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label>Contact Email</Label>
                                            <Input 
                                                value={settings.contactEmail || ''} 
                                                onChange={(e) => setSettings({...settings, contactEmail: e.target.value})} 
                                                disabled={!isEditing} 
                                                placeholder="e.g. hello@sonoma.com"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone Number</Label>
                                            <Input 
                                                value={settings.contactPhone || ''} 
                                                onChange={(e) => setSettings({...settings, contactPhone: e.target.value})} 
                                                disabled={!isEditing} 
                                                placeholder="e.g. (555) 123-4567"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2 mt-4">
                                        <Label>Physical Address</Label>
                                        <Textarea 
                                            value={settings.contactAddress || ''} 
                                            onChange={(e) => setSettings({...settings, contactAddress: e.target.value})} 
                                            disabled={!isEditing} 
                                            rows={2} 
                                            placeholder="e.g. 123 Education Lane, San Francisco, CA"
                                        />
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-medium flex items-center gap-2"><Globe className="h-4 w-4" /> Social Media Links</h3>
                                        <div className="flex items-center space-x-2">
                                            <Switch checked={settings.showSocialLinks} onCheckedChange={(checked) => setSettings({...settings, showSocialLinks: checked})} disabled={!isEditing} />
                                            <Label>Show Links</Label>
                                        </div>
                                    </div>
                                    {settings.showSocialLinks && (
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2"><Label>Facebook URL</Label><Input value={settings.facebookUrl || ''} onChange={(e) => setSettings({...settings, facebookUrl: e.target.value})} disabled={!isEditing} placeholder="https://facebook.com/..." /></div>
                                            <div className="space-y-2"><Label>Twitter (X) URL</Label><Input value={settings.twitterUrl || ''} onChange={(e) => setSettings({...settings, twitterUrl: e.target.value})} disabled={!isEditing} placeholder="https://twitter.com/..." /></div>
                                            <div className="space-y-2"><Label>Instagram URL</Label><Input value={settings.instagramUrl || ''} onChange={(e) => setSettings({...settings, instagramUrl: e.target.value})} disabled={!isEditing} placeholder="https://instagram.com/..." /></div>
                                            <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={settings.linkedinUrl || ''} onChange={(e) => setSettings({...settings, linkedinUrl: e.target.value})} disabled={!isEditing} placeholder="https://linkedin.com/..." /></div>
                                        </div>
                                    )}
                                </div>
                                {isEditing && (
                                    <div className="flex justify-end pt-4">
                                        <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Saving..." : <><Save className="h-4 w-4 mr-2" /> Save Changes</>}</Button>
                                    </div>
                                )}
                             </CardContent>
                         </Card>
                    </TabsContent>

                    {/* Features Tab */}
                    <TabsContent value="features" className="space-y-6">
                        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-1">
                                         <CardTitle className="text-xl flex items-center gap-2">
                                             <Sparkles className="h-5 w-5 text-purple-500" />
                                             Features Section
                                         </CardTitle>
                                         <CardDescription>
                                             Manage features, descriptions, and icons.
                                         </CardDescription>
                                     </div>
                                     <div className="flex items-center gap-2">
                                        {!isEditing ? (
                                            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" className="gap-2"><Edit className="h-4 w-4" /> Edit</Button>
                                        ) : (
                                            <Button onClick={() => { setIsEditing(false); loadFeatures(); }} variant="ghost" size="sm" className="gap-2 text-red-500"><X className="h-4 w-4" /> Cancel</Button>
                                        )}
                                     </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-8">
                                <div className="space-y-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label>Section Heading</Label>
                                            <Input value={featuresConfig.heading} onChange={(e) => setFeaturesConfig({...featuresConfig, heading: e.target.value})} disabled={!isEditing} className="font-semibold" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Subheading</Label>
                                            <Input value={featuresConfig.subheading} onChange={(e) => setFeaturesConfig({...featuresConfig, subheading: e.target.value})} disabled={!isEditing} />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                     <div className="flex items-center justify-between">
                                         <h3 className="text-lg font-medium">Feature Cards</h3>
                                         {isEditing && (
                                            <Button onClick={addFeatureItem} size="sm" variant="outline" className="gap-2"><Plus className="h-4 w-4" /> Add Feature</Button>
                                         )}
                                     </div>
                                     
                                     <div className="grid gap-6 md:grid-cols-2">
                                         {featuresConfig.features.map((feature, index) => {
                                             const IconComponent = iconMap[feature.icon] || Sparkles;
                                             return (
                                                <div key={index} className="relative rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-4 space-y-4">
                                                    {isEditing && (
                                                        <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => removeFeatureItem(index)}>
                                                            <Trash className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    
                                                    <div className="grid gap-2">
                                                        <Label>Title</Label>
                                                        <Input value={feature.title} onChange={(e) => updateFeatureItem(index, "title", e.target.value)} disabled={!isEditing} className="font-bold" />
                                                    </div>

                                                    <div className="grid gap-2">
                                                        <Label>Description</Label>
                                                        <Textarea value={feature.description} onChange={(e) => updateFeatureItem(index, "description", e.target.value)} disabled={!isEditing} rows={2} />
                                                    </div>
                                                    
                                                    <div className="grid gap-2">
                                                         <Label>Icon</Label>
                                                         <div className="flex items-center gap-3">
                                                             <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                                                                <IconComponent className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                                                             </div>
                                                             <Select 
                                                                disabled={!isEditing} 
                                                                value={feature.icon} 
                                                                onValueChange={(val) => updateFeatureItem(index, "icon", val)}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Select icon" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {Object.keys(iconMap).map((iconKey) => (
                                                                        <SelectItem key={iconKey} value={iconKey}>
                                                                            <div className="flex items-center gap-2">
                                                                                <span>{iconKey}</span>
                                                                            </div>
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                             </Select>
                                                         </div>
                                                    </div>
                                                </div>
                                             );
                                         })}
                                     </div>
                                </div>
                                
                                {isEditing && (
                                    <div className="flex justify-end pt-4">
                                        <Button onClick={saveFeatures} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Saving..." : <><Save className="h-4 w-4 mr-2" /> Save Features</>}</Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Pricing Tab */}
                    <TabsContent value="pricing" className="space-y-6">
                        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-1">
                                         <CardTitle className="text-xl flex items-center gap-2">
                                             <DollarSign className="h-5 w-5 text-green-500" />
                                             Pricing Plans
                                         </CardTitle>
                                         <CardDescription>Manage pricing tiers.</CardDescription>
                                     </div>
                                     <div className="flex items-center gap-2">
                                        {!isEditing ? (
                                            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" className="gap-2"><Edit className="h-4 w-4" /> Edit</Button>
                                        ) : (
                                            <Button onClick={() => { setIsEditing(false); loadPricing(); }} variant="ghost" size="sm" className="gap-2 text-red-500"><X className="h-4 w-4" /> Cancel</Button>
                                        )}
                                     </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-8">
                                <div className="space-y-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label>Section Heading</Label>
                                            <Input value={pricingConfig.heading} onChange={(e) => setPricingConfig({...pricingConfig, heading: e.target.value})} disabled={!isEditing} className="font-semibold" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Subheading</Label>
                                            <Input value={pricingConfig.subheading} onChange={(e) => setPricingConfig({...pricingConfig, subheading: e.target.value})} disabled={!isEditing} />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                     <div className="flex items-center justify-between">
                                         <h3 className="text-lg font-medium">Plans</h3>
                                         {isEditing && (
                                            <Button onClick={addPlan} size="sm" variant="outline" className="gap-2"><Plus className="h-4 w-4" /> Add Plan</Button>
                                         )}
                                     </div>
                                     
                                     <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                                         {pricingConfig.plans.map((plan, index) => (
                                             <div key={index} className="relative rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-4 space-y-4">
                                                 {isEditing && (
                                                     <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => removePlan(index)}>
                                                         <Trash className="h-4 w-4" />
                                                     </Button>
                                                 )}
                                                 
                                                 <div className="grid gap-2">
                                                     <Label>Plan Name</Label>
                                                     <Input value={plan.name} onChange={(e) => updatePlan(index, "name", e.target.value)} disabled={!isEditing} className="font-bold" />
                                                 </div>
                                                 
                                                 <div className="grid grid-cols-2 gap-2">
                                                     <div className="space-y-1"><Label className="text-xs">Price</Label><Input value={plan.price} onChange={(e) => updatePlan(index, "price", e.target.value)} disabled={!isEditing} /></div>
                                                     <div className="space-y-1"><Label className="text-xs">Badge</Label><Input value={plan.badge || ""} onChange={(e) => updatePlan(index, "badge", e.target.value)} disabled={!isEditing} placeholder="e.g. POPULAR" /></div>
                                                 </div>

                                                 <div className="space-y-1"><Label className="text-xs">Capacity</Label><Input value={plan.capacity} onChange={(e) => updatePlan(index, "capacity", e.target.value)} disabled={!isEditing} /></div>

                                                 <div className="space-y-2">
                                                     <Label className="text-xs">Features</Label>
                                                     {plan.features.map((feature, idx) => (
                                                         <div key={idx} className="flex gap-2">
                                                             <Input value={feature} onChange={(e) => updateFeatureInPlan(index, idx, e.target.value)} disabled={!isEditing} className="h-8 text-sm" />
                                                             {isEditing && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => removeFeatureFromPlan(index, idx)}><Trash className="h-3 w-3" /></Button>}
                                                         </div>
                                                     ))}
                                                     {isEditing && <Button variant="ghost" size="sm" className="w-full h-8 text-xs dashed border border-gray-300 dark:border-gray-600" onClick={() => addFeatureToPlan(index)}><Plus className="h-3 w-3 mr-1" /> Add Feature</Button>}
                                                 </div>
                                                 
                                                 <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                                     <Label className="text-xs">Call to Action</Label>
                                                     <div className="flex gap-2">
                                                         <Input value={plan.cta} onChange={(e) => updatePlan(index, "cta", e.target.value)} disabled={!isEditing} className="flex-1" />
                                                         {isEditing && (
                                                             <Button variant="outline" size="sm" onClick={() => updatePlan(index, "ctaStyle", plan.ctaStyle === "primary" ? "muted" : "primary")} className={plan.ctaStyle === "primary" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}>
                                                                 {plan.ctaStyle === "primary" ? "Blue" : "Gray"}
                                                             </Button>
                                                         )}
                                                     </div>
                                                 </div>
                                             </div>
                                         ))}
                                     </div>
                                </div>
                                
                                {isEditing && (
                                    <div className="flex justify-end pt-4 gap-3">
                                        <Button onClick={savePricingDraft} disabled={saving} variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                                            {saving ? "Saving..." : "Save Draft"}
                                        </Button>
                                         <Button onClick={publishPricing} disabled={publishing} className="bg-blue-600 hover:bg-blue-700 text-white">
                                            {publishing ? "Publishing..." : <><Globe className="h-4 w-4 mr-2" /> Publish Live</>}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default LandingPageSettings;
